import { useState } from "react";
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import Modal from "./Modal";
import {
  useAdminRequestApproval,
  useAdminRequestReject,
  useCancelAssessment,
} from "../custom-hooks/queries";
import ModalWithTextBox from "./ModalWithTextBox";
import NotificationModal from "./NotificationModal";
import CancelAssessmentModal from "./CancelAssessmentModal";
import {
  canCloseAssessmentFromStatus,
  isPublishedLibraryStatus,
} from "../utils/assessmentAdminStatusHelpers";

export default function CheckedDropdown({
  publishingOptions,
  setSelectedStatus,
  status,
  vqaId,
  vqaDetailsRefetch,
  setIsSelected,
  submit,
  setNoCap,
  id,
  sponsors,
}) {
  const [selectedIndex, setSelectedIndex] = useState(null); // No selection by default
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRejectModal, setIsRejectModal] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null); // Store pending selection
  const [message, setMessage] = useState(""); // Store pending selection
  const [show, setShow] = useState(false);
  const [isCancelModal, setIsCancelModal] = useState(false);

  const shouldDisableField = sponsors?.some((sponsor) =>
    [
      "Final Report Delivered",
      "Report Anonymized",
      "Report Published to Library",
    ].includes(sponsor?.sponsor_status)
  );

  const areAllSponsorsCancelled = sponsors?.every(
    (sponsor) => sponsor.sponsor_status === "Canceled Assessment"
  );
  const { mutate: cancelAssessment } = useCancelAssessment();

  const handleSelection = (selected) => {
    if (["Request Accepted"].includes(selected.title)) {
      setPendingSelection(selected); // Store the selection for confirmation
      setMessage("Do you want to approve this Assessment request?");
      setIsModalOpen(true); // Open confirmation modal
    } else if (["Assessment in Progress"].includes(selected.title)) {
      setNoCap(true);
      setPendingSelection(selected); // Store the selection for confirmation
      setMessage("Do you want to change status to Assessment in Progress?");

      setIsModalOpen(true); // Open confirmation modal
    } else if (["Assessment Complete"].includes(selected.title)) {
      setNoCap(true);
      setPendingSelection(selected); // Store the selection for confirmation
      setMessage("Do you want Assessment to be Completed?");

      setIsModalOpen(true); // Open confirmation modal
    } else if (["Report Published to Library", "Published to Library"].includes(selected.title)) {
      setNoCap(true);
      setPendingSelection(selected); // Store the selection for confirmation
      setMessage("Do you want to Publish Report to Library?");

      setIsModalOpen(true); // Open confirmation modal
    } else if (["Assessment Closed"].includes(selected.title)) {
      setNoCap(true);
      setPendingSelection(selected);
      setMessage("Do you want to close this Assessment?");
      setIsModalOpen(true);
    } else if (["Request Declined"].includes(selected.title)) {
      setPendingSelection(selected); // Store the selection for confirmation
      setIsRejectModal(true); // Open confirmation modal
    } else if (["Canceled Assessment"].includes(selected.title)) {
      setPendingSelection(selected);
      setIsCancelModal(true); // Open the new cancel modal instead
    } else {
      confirmSelection(selected); // Directly confirm selection for other statuses
    }
  };

  const { mutate: approve } = useAdminRequestApproval();

  const { mutate: reject } = useAdminRequestReject();

  const { mutate: VqaCancel, isError: vqaCancelError } = useCancelAssessment();
  const currentStatus = status;
  const isReportPublishedToLibrary = isPublishedLibraryStatus(status);

  const confirmSelection = (selected) => {
    const index = publishingOptions.findIndex(
      (option) => option.title === selected.title
    );
    setSelectedIndex(index); // Update the index on selection
    setSelectedStatus(selected.title); // Update the status
    setIsSelected(false);
    if (selected.title === "Request Accepted") {
      approve(vqaId, {
        onSuccess: () => {
          vqaDetailsRefetch();
        },
      });
    } else {
      // Persist every other workflow step (e.g. Draft Report Delivered) via admin VQA update.
      // Previously only a few titles called submit(), so picking the next phase did nothing visible.
      submit(
        isPublishedLibraryStatus(selected?.title)
          ? "Report Published to Library"
          : selected?.title
      );
    }
    setIsModalOpen(false); // Close the modal
  };

  const cancelSelection = () => {
    setPendingSelection(null); // Clear pending selection
    setIsModalOpen(false); // Close the modal
  };

  return (
    <>
      <Listbox
        value={selectedIndex !== null ? publishingOptions[selectedIndex] : null}
        onChange={handleSelection}
      >
        <Label className="sr-only">Change published status</Label>
        <div className="relative">
          {/* One ListboxButton: previously only the chevron opened the menu, so clicks on the label did nothing. */}
          <ListboxButton
            className={`inline-flex items-stretch overflow-hidden rounded-md outline-none focus-visible:outline focus-visible:outline-2 ${
              status === "Canceled Assessment" || status === "Cancelled"
                ? "divide-x divide-red-600 focus-visible:outline-red-400"
                : "divide-x divide-indigo-600 focus-visible:outline-indigo-400"
            }`}
          >
            <span
              className={`inline-flex items-center gap-x-1.5 px-3 py-2 text-sm font-semibold text-white ${
                status === "Canceled Assessment" || status === "Cancelled"
                  ? "bg-red-400"
                  : "bg-indigo-600"
              }`}
            >
              <span className="sr-only">Change published status</span>
              {currentStatus}
            </span>
            <span
              className={`inline-flex items-center p-2 text-white ${
                status === "Canceled Assessment" || status === "Cancelled"
                  ? "bg-red-400"
                  : "bg-indigo-600"
              }`}
              aria-hidden
            >
              <ChevronDownIcon className="size-5 forced-colors:text-[Highlight]" />
            </span>
          </ListboxButton>

          <ListboxOptions
            transition
            className="absolute right-0 z-10 mt-2 w-72 h-96 origin-top-right divide-y divide-gray-200 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {publishingOptions.map((option, index) => {
              const currentIndex = publishingOptions.findIndex(
                (opt) => opt.title === status
              );
              const assessmentClosedAllowed =
                option.title === "Assessment Closed" &&
                canCloseAssessmentFromStatus(status);

              const isPending = status === "Pending Diligent Review";

              // When status is Report Published to Library, disable all options
              if (isReportPublishedToLibrary && !assessmentClosedAllowed) {
                return (
                  <ListboxOption
                    key={option.title}
                    value={option}
                    disabled={true}
                    className="group cursor-default select-none p-4 text-sm border border-gray-300 opacity-50 cursor-not-allowed"
                  >
                    <div className="flex flex-col">
                      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                        <p className="font-normal">{option?.title}</p>
                        {index <= currentIndex && (
                          <span className="text-green-600 justify-self-end">
                            {option?.title !== "Request Declined" &&
                              status !== "Denied" &&
                              status !== "Canceled Assessment" && (
                                <CheckIcon
                                  aria-hidden="true"
                                  className="size-5"
                                />
                              )}
                          </span>
                        )}
                      </div>
                    </div>
                  </ListboxOption>
                );
              }

              // Sequential workflow: from "Assessment Scheduled" through "Report Anonymized",
              // only the next status is selectable (not past steps, not skipping ahead).
              // Indices 8–11 were missing before, which disabled every option after "Assessment Complete".
              const usesSequentialStatusRule =
                currentIndex >= 6 && currentIndex <= 12;
              let isDisabled = usesSequentialStatusRule
                ? index > currentIndex + 1 || index <= currentIndex
                : true;

              if (isPending) {
                isDisabled = !["Request Accepted", "Request Declined"].includes(
                  option.title
                );
              }

              const isCancelAssessmentAllowed =
                option.title === "Canceled Assessment" &&
                !shouldDisableField && // Disable if all sponsors have uploaded final report
                !areAllSponsorsCancelled && // Disable if all sponsors are cancelled
                currentIndex >=
                  publishingOptions.findIndex(
                    (opt) => opt.title === "Request Accepted"
                  );
              const isPublishReportAllowed =
                isPublishedLibraryStatus(option.title) &&
                !areAllSponsorsCancelled &&
                status === "Report Anonymized";

              const isSelectedOrBefore = index <= currentIndex;
              const optionDisabled =
                isDisabled &&
                !isCancelAssessmentAllowed &&
                !isPublishReportAllowed &&
                !assessmentClosedAllowed;

              return (
                <ListboxOption
                  key={option.title}
                  value={option}
                  disabled={optionDisabled}
                  className={`group cursor-default select-none p-4 text-sm border border-gray-300 ${
                    optionDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                      <p className="font-normal">{option?.title}</p>

                      {isSelectedOrBefore && (
                        <span
                          className={`${
                            isSelectedOrBefore
                              ? "text-green-600"
                              : "text-indigo-600"
                          } justify-self-end`}
                        >
                          {option?.title !== "Request Declined" &&
                            status !== "Denied" &&
                            status !== "Canceled Assessment" && (
                              <CheckIcon
                                aria-hidden="true"
                                className="size-5"
                              />
                            )}
                        </span>
                      )}

                      {(status === "Denied" &&
                        option?.title === "Request Declined") ||
                      (status === "Canceled Assessment" &&
                        option?.title === "Canceled Assessment") ? (
                        <XMarkIcon
                          aria-hidden="true"
                          className="size-5 text-red-600 justify-self-end"
                        />
                      ) : null}
                    </div>
                  </div>
                </ListboxOption>
              );
            })}
          </ListboxOptions>
        </div>
      </Listbox>

      {/* Confirmation Modal */}
      <Modal
        setOpen={setIsModalOpen}
        open={isModalOpen}
        description={message}
        title={"Confirm"}
        onClick={() => confirmSelection(pendingSelection)}
        textBtn={"Yes"}
      />

      <ModalWithTextBox
        open={isRejectModal}
        setOpen={setIsRejectModal}
        onClick={reject}
        id={vqaId}
        refetch={vqaDetailsRefetch}
      />
      <CancelAssessmentModal
        open={isCancelModal}
        setOpen={setIsCancelModal}
        onClick={cancelAssessment}
        sponsors={sponsors}
        id={id}
        refetch={vqaDetailsRefetch}
      />
      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={"Error"}
        error={true}
      />
    </>
  );
}
